import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { CloudOff, CloudSun, Info, RotateCw, X } from "lucide-react";
import { apiFetch } from "@/lib/api-client";
import { WarningCard } from "@/components/ui/warning-card";

/**
 * ForecastTodayCard — B5, "Heute erwartet".
 *
 * Zeigt den erwarteten Außen-Höchstwert für heute (Open-Meteo),
 * ASR-ETA-Zeiten (wann 26/30/35 °C außen erreicht werden) und
 * optional den Info-Hinweis "2. Messung am Nachmittag sinnvoll".
 *
 * Farbwelt: cool-teal durchgängig — NIEMALS heat-orange/amber.
 * Begründung: Prognose ≠ Messung. Orange = gemessene Gefahr.
 * Cool-teal = Trust/Prognose. Diese Trennung ist haftungsrelevant.
 *
 * Red-Lines:
 *   1. Keine prognostizierte Stufenzahl — Phase 1 outdoor-only.
 *   2. "Prognose ersetzt Pflicht-Messung nicht" immer sichtbar.
 *   5. 2-Mess-Hinweis = Angebot, kein Pflicht-Counter.
 *   7. Keine heat-Farbe irgendwo in dieser Karte.
 */

// ─── API-Contract-Typen ────────────────────────────────────────────────────

interface ForecastResponse {
  generated_at: string;
  has_forecast: boolean;
  peak_temp_c: number | null;
  peak_time: string | null;
  asr_eta: {
    soll: string | null;
    muss: string | null;
    kritisch: string | null;
  };
  forecast_peak_level: "normal" | "soll" | "muss" | "kritisch";
  measured_today_level: "normal" | "soll" | "muss" | "kritisch" | null;
  zweite_messung_sinnvoll: boolean;
}

// ─── Helper: ETA-Zeit formatieren ─────────────────────────────────────────

/**
 * Formatiert einen "HH:MM"-String oder ISO-Zeitstempel zu "HH:MM Uhr".
 * Das Backend liefert bereits Europe/Berlin-Lokalzeit — kein TZ-Rendering.
 */
function formatEtaTime(eta: string): string {
  // "HH:MM"-Format direkt verwenden (kein Date-Parsing nötig)
  if (/^\d{2}:\d{2}$/.test(eta)) {
    return `${eta} Uhr`;
  }
  // ISO-String: via Intl formatieren
  try {
    const date = new Date(eta);
    if (!Number.isNaN(date.getTime())) {
      return (
        new Intl.DateTimeFormat("de-DE", {
          hour: "2-digit",
          minute: "2-digit",
        }).format(date) + " Uhr"
      );
    }
  } catch {
    // Fallback: Rohwert
  }
  return eta;
}

/**
 * Formatiert den Stand-Zeitstempel (ISO RFC3339) zu "HH:MM".
 */
function formatStand(iso: string): string {
  try {
    const date = new Date(iso);
    if (!Number.isNaN(date.getTime())) {
      return new Intl.DateTimeFormat("de-DE", { timeStyle: "short" }).format(date);
    }
  } catch {
    // ignorieren
  }
  return iso;
}

// ─── EtaRow ────────────────────────────────────────────────────────────────

interface EtaRowProps {
  schwelle: string;
  eta: string | null | undefined;
  hint: string;
}

/**
 * EtaRow — eine Zeile in der ASR-ETA-Zeitleiste.
 *
 * Schwellen-Pill BEWUSST cool-teal (nicht heat-farbig),
 * auch für die 35-°C-Zeile — es ist eine Prognose, keine Stufe.
 *
 * ARIA: aria-label auf <li> fasst Pill + Text zusammen (verhindert
 * fragmentiertes Vorlesen), visuelle Spans aria-hidden.
 */
function EtaRow({ schwelle, eta, hint }: EtaRowProps): React.ReactElement {
  const reached = typeof eta === "string" && eta.length > 0;
  const timeLabel = reached ? formatEtaTime(eta as string) : null;

  // Zusammenfassendes aria-label für Screenreader
  const ariaLabel = reached
    ? `${schwelle} Außentemperatur ab ${timeLabel} — Außen-Schwelle ${hint}, noch keine Raum-Stufe`
    : `${schwelle} Außentemperatur heute nicht erwartet`;

  return (
    <li
      aria-label={ariaLabel}
      className="flex min-h-[var(--size-touch-min)] items-center gap-3 rounded-md px-1 py-1.5"
    >
      {/* Schwellen-Pill */}
      <span
        aria-hidden="true"
        className="inline-flex w-[4.5rem] shrink-0 justify-center rounded-full bg-[color:var(--color-cool-teal-100)] px-2 py-1 text-xs font-semibold text-[color:var(--color-cool-teal-800)] [font-variant-numeric:tabular-nums]"
      >
        {schwelle}
      </span>
      <span aria-hidden="true" className="flex-1 text-sm [font-variant-numeric:tabular-nums]">
        {reached ? (
          <span className="text-[color:var(--color-cool-teal-900)]">
            ab <strong className="font-semibold">{timeLabel}</strong>
            <span className="ml-1 text-xs text-[color:var(--color-cool-teal-700)]">· {hint}</span>
          </span>
        ) : (
          <span className="text-[color:var(--color-cool-teal-700)]">heute nicht erwartet</span>
        )}
      </span>
    </li>
  );
}

// ─── DismissableHint ────────────────────────────────────────────────────────

/**
 * 2-Mess-Hinweis-Inline-Block.
 *
 * Trigger-Bedingung: `zweite_messung_sinnvoll === true` aus dem API-Contract.
 * Dismiss: localStorage-Key inkl. ISO-Datum (YYYY-MM-DD) → tagesbegrenzt.
 * severity=info (cool-teal, nie amber/heat) — Angebot, kein Pflicht-Counter.
 */

function getTodayDateKey(): string {
  const today = new Date();
  const y = today.getFullYear();
  const m = String(today.getMonth() + 1).padStart(2, "0");
  const d = String(today.getDate()).padStart(2, "0");
  return `hp.forecast.2ndmeasure.dismissed.${y}-${m}-${d}`;
}

function isDismissedToday(): boolean {
  try {
    return localStorage.getItem(getTodayDateKey()) === "1";
  } catch {
    return false;
  }
}

function dismissToday(): void {
  try {
    localStorage.setItem(getTodayDateKey(), "1");
  } catch {
    // localStorage nicht verfügbar (private mode etc.) — ignorieren
  }
}

interface DismissableHintProps {
  peakHourFormatted: string | null;
}

function DismissableHint({ peakHourFormatted }: DismissableHintProps): React.ReactElement | null {
  const [dismissed, setDismissed] = React.useState<boolean>(() => isDismissedToday());

  function handleDismiss(): void {
    dismissToday();
    setDismissed(true);
  }

  if (dismissed) return null;

  const bodyText =
    peakHourFormatted !== null
      ? `Heute wird es voraussichtlich deutlich wärmer als am Vormittag. Eine Messung gegen ${peakHourFormatted} bildet die Tagesbelastung genauer ab. Freiwillig — keine Pflicht.`
      : "Heute wird es voraussichtlich deutlich wärmer als am Vormittag. Eine zweite Messung im Tagesverlauf bildet die Belastung genauer ab. Freiwillig — keine Pflicht.";

  return (
    <div className="mt-4 border-t border-[color:var(--color-cool-teal-200)] pt-4">
      <div role="status" className="flex gap-3">
        <Info
          aria-hidden="true"
          strokeWidth={2}
          className="mt-0.5 size-5 shrink-0 text-[color:var(--color-info)]"
        />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-[color:var(--color-cool-teal-900)]">
            Eine zweite Messung am Nachmittag kann sinnvoll sein
          </p>
          <p className="mt-1 text-sm leading-normal text-[color:var(--color-cool-teal-800)]">
            {bodyText}
          </p>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Hinweis zur zweiten Messung für heute ausblenden"
          className="-m-2 inline-flex size-11 shrink-0 items-center justify-center rounded-md text-[color:var(--color-cool-teal-700)] hover:bg-[color:var(--color-cool-teal-100)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-border-focus)]"
        >
          <X aria-hidden="true" strokeWidth={2.25} className="size-5" />
        </button>
      </div>
    </div>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────

function ForecastSkeleton(): React.ReactElement {
  return (
    <section
      aria-busy="true"
      aria-label="Tages-Prognose wird geladen"
      className="rounded-2xl border border-[color:var(--color-cool-teal-200)] bg-[color:var(--color-cool-teal-50)] p-6 shadow-[var(--shadow-md)] md:p-8"
    >
      <div className="h-4 w-32 animate-pulse rounded bg-[color:var(--color-cool-teal-100)]" />
      <div className="mt-3 h-4 w-64 animate-pulse rounded bg-[color:var(--color-cool-teal-100)]" />
      <div className="mt-5 grid gap-6 md:grid-cols-2">
        <div className="h-20 animate-pulse rounded-lg bg-[color:var(--color-cool-teal-100)]" />
        <div className="h-20 animate-pulse rounded-lg bg-[color:var(--color-cool-teal-100)]" />
      </div>
    </section>
  );
}

// ─── Fehler-State ─────────────────────────────────────────────────────────

interface ForecastErrorStateProps {
  onRetry: () => void;
}

function ForecastErrorState({ onRetry }: ForecastErrorStateProps): React.ReactElement {
  return (
    <section className="rounded-2xl border border-[color:var(--color-cool-teal-200)] bg-[color:var(--color-cool-teal-50)] p-6 shadow-[var(--shadow-md)]">
      <header className="flex items-center gap-2">
        <CloudOff
          aria-hidden="true"
          strokeWidth={2}
          className="size-5 text-[color:var(--color-cool-teal-700)]"
        />
        <h2 className="text-sm font-semibold text-[color:var(--color-cool-teal-900)]">
          Tages-Prognose gerade nicht abrufbar
        </h2>
      </header>
      <p className="mt-2 text-sm text-[color:var(--color-cool-teal-800)]">
        Der Wetterdienst ist momentan nicht erreichbar. Die Prognose ist eine
        Orientierungshilfe — Ihre Dokumentation per Raum-Messung ist davon nicht betroffen.
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-3 inline-flex min-h-[var(--size-touch-min)] items-center gap-1 rounded-md -mx-2 px-2 text-sm font-medium text-[color:var(--color-cool-teal-800)] underline-offset-4 hover:underline"
      >
        Erneut versuchen
        <RotateCw aria-hidden="true" strokeWidth={2.25} className="size-4" />
      </button>
    </section>
  );
}

// ─── Leer-State (kein Standort) ────────────────────────────────────────────

/**
 * Rendert nur, wenn has_forecast=false UND die globale PLZ-WarningCard
 * (dashboard-route Z.202-209) nicht greift.
 * `orgHasPlz`: true wenn die übergeordnete Route bereits eine PLZ-Warnung zeigt.
 * In diesem Fall → null (Doppelung vermeiden).
 */
interface ForecastNoLocationProps {
  orgHasPlz: boolean;
}

function ForecastNoLocation({ orgHasPlz }: ForecastNoLocationProps): React.ReactElement | null {
  if (orgHasPlz) {
    // Die globale PLZ-WarningCard ist bereits sichtbar — Forecast-Karte nicht doppelt zeigen.
    return null;
  }
  return (
    <WarningCard
      severity="info"
      icon={CloudSun}
      title="Tages-Prognose nicht verfügbar"
      description="Ergänzen Sie die PLZ Ihrer Einrichtung, dann zeigen wir Ihnen den erwarteten Außen-Höchstwert für heute."
      action={{ label: "PLZ ergänzen", href: "/einstellungen" }}
    />
  );
}

// ─── Hauptkarte ────────────────────────────────────────────────────────────

interface ForecastTodayCardProps {
  /** true wenn die Eltern-Route bereits eine PLZ-WarningCard zeigt (Doppelung verhindern) */
  orgHasPlz: boolean;
}

/**
 * ForecastTodayCard — eigenständige Komponente mit eigener TanStack-Query.
 * Koppelt NICHT an den /dashboard/status-call.
 * staleTime: 10min (Forecast ändert sich langsam).
 * Abstand vom Eltern-Container (space-y-6) — keine eigenen mt-Margins.
 */
export function ForecastTodayCard({ orgHasPlz }: ForecastTodayCardProps): React.ReactElement | null {
  const forecast = useQuery({
    queryKey: ["dashboard", "forecast"],
    queryFn: () => apiFetch<ForecastResponse>("/v1/dashboard/forecast"),
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  // Lade-State
  if (forecast.isPending) {
    return <ForecastSkeleton />;
  }

  // Fehler-State (upstream-fail, Netzwerkfehler, 500)
  if (forecast.isError) {
    return <ForecastErrorState onRetry={() => void forecast.refetch()} />;
  }

  const data = forecast.data;

  // Kein Standort / kein Forecast verfügbar
  if (!data.has_forecast) {
    return <ForecastNoLocation orgHasPlz={orgHasPlz} />;
  }

  // Peak-Werte
  const peakC = data.peak_temp_c;
  const peakFormatted =
    peakC !== null
      ? new Intl.NumberFormat("de-DE", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 1,
        }).format(peakC)
      : "—";
  const peakHourFormatted =
    data.peak_time !== null ? formatEtaTime(data.peak_time) : null;

  const standFormatted = formatStand(data.generated_at);

  // Sonderfall kühl: Peak unter 26 °C → ETA-Zeilen durch Kurztext ersetzen
  const isCoolDay = peakC !== null && peakC < 26;

  return (
    <section
      aria-labelledby="forecast-today-heading"
      className="anim-slide-in-top rounded-2xl border border-[color:var(--color-cool-teal-200)] bg-[color:var(--color-cool-teal-50)] p-6 shadow-[var(--shadow-md)] md:p-8"
    >
      {/* (a) Titel-Zeile */}
      <header className="flex items-center gap-2">
        <CloudSun
          aria-hidden="true"
          strokeWidth={2.25}
          className="size-5 text-[color:var(--color-cool-teal-700)]"
        />
        <h2
          id="forecast-today-heading"
          className="text-xs font-semibold uppercase tracking-[var(--letter-spacing-wide)] text-[color:var(--color-cool-teal-800)]"
        >
          Heute erwartet
        </h2>
      </header>

      {/* (b) Pflicht-Rahmung — DIREKT unter Titel, VOR der Peak-Zahl */}
      <p className="mt-2 flex items-start gap-1.5 text-sm font-medium leading-snug text-[color:var(--color-cool-teal-800)]">
        <Info aria-hidden="true" strokeWidth={2} className="mt-0.5 size-4 shrink-0" />
        <span>Prognose (Open-Meteo) — ersetzt die Pflicht-Messung nicht.</span>
      </p>

      {/* (c+d) Peak-Block + ETA-Zeitleiste: 2-spaltig auf md */}
      <div className="mt-5 grid gap-6 md:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
        {/* (c) Peak-Block */}
        <div>
          <p className="text-xs font-medium text-[color:var(--color-cool-teal-800)]">
            Außen-Höchstwert
          </p>
          <p className="mt-1 flex items-baseline gap-2">
            <span className="text-[length:var(--text-display)] font-bold leading-none tracking-[var(--letter-spacing-tight)] text-[color:var(--color-cool-teal-900)] [font-variant-numeric:tabular-nums]">
              {peakFormatted}
            </span>
            <span className="text-2xl font-semibold text-[color:var(--color-cool-teal-700)]">°C</span>
          </p>
          {peakHourFormatted !== null && (
            <p className="mt-1.5 text-sm text-[color:var(--color-cool-teal-800)]">
              voraussichtlich gegen{" "}
              <strong className="font-semibold [font-variant-numeric:tabular-nums]">
                {peakHourFormatted}
              </strong>
            </p>
          )}
        </div>

        {/* (d) ETA-Zeitleiste oder Kühl-Hinweis */}
        <div className="md:mt-0">
          {isCoolDay ? (
            <p className="text-sm text-[color:var(--color-cool-teal-800)]">
              Heute keine Hitzebelastung erwartet — alle ASR-Schwellen bleiben unterschritten.
            </p>
          ) : (
            <>
              <p className="text-xs font-medium text-[color:var(--color-cool-teal-800)]">
                Außen-Schwellen heute (nicht Raum-Stufe)
              </p>
              <ol className="mt-2 space-y-1.5">
                <EtaRow schwelle="26 °C" eta={data.asr_eta.soll} hint="Soll" />
                <EtaRow schwelle="30 °C" eta={data.asr_eta.muss} hint="Muss" />
                <EtaRow schwelle="35 °C" eta={data.asr_eta.kritisch} hint="kritisch" />
              </ol>
              <p className="mt-2.5 text-xs leading-snug text-[color:var(--color-cool-teal-700)]">
                Bezieht sich auf die <strong>Außentemperatur</strong>. Die maßgebliche Stufe entsteht
                erst aus Ihrer Raum-Messung.
              </p>
            </>
          )}
        </div>
      </div>

      {/* Stufe-3-Vorwarnung: stündliche Messung bei Kritisch (Red-Line 4, ASR A3.5).
          Greift sobald 35 °C außen heute erwartet werden — informativer Rechtshinweis,
          KEIN Pflicht-Counter. */}
      {data.asr_eta.kritisch !== null && (
        <div
          role="status"
          className="mt-4 flex gap-2 rounded-lg bg-[color:var(--color-cool-teal-100)] px-3 py-2.5"
        >
          <Info
            aria-hidden="true"
            strokeWidth={2}
            className="mt-0.5 size-4 shrink-0 text-[color:var(--color-cool-teal-700)]"
          />
          <p className="text-xs leading-snug text-[color:var(--color-cool-teal-800)]">
            Bei Stufe <strong>Kritisch</strong> (ab 35 °C gemessener Raumtemperatur) ist
            stündliche Messung erforderlich (ASR A3.5). Die Außen-Prognose ersetzt diese
            Pflicht nicht.
          </p>
        </div>
      )}

      {/* 2-Mess-Hinweis: nur bei zweite_messung_sinnvoll=true + nicht-kritisch-gemessen */}
      {data.zweite_messung_sinnvoll && data.measured_today_level !== "kritisch" && (
        <DismissableHint peakHourFormatted={peakHourFormatted} />
      )}

      {/* (e) Stand-Zeile + Quelle */}
      <footer className="mt-5 border-t border-[color:var(--color-cool-teal-200)] pt-3">
        <p className="text-xs text-[color:var(--color-cool-teal-700)]">
          Stand: {standFormatted} · Quelle: Open-Meteo (CC-BY 4.0)
        </p>
      </footer>
    </section>
  );
}

ForecastTodayCard.displayName = "ForecastTodayCard";
